/**
 * Upgrade prompt for the plan gate. Renders nothing until a workspace write
 * comes back `402 plan_required` or `team_plan_required`; then shows a dismissible banner at the top
 * of the content column. This is an error state — the write did not happen —
 * not onboarding chrome, so it only ever appears in response to a rejected
 * request and goes away on dismiss.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Lock, X } from "lucide-react";
import {
  installPlanRequiredInterceptor,
  PLAN_REQUIRED_EVENT,
  type PlanRequiredDetail,
} from "../lib/plan-gate";

export function PlanRequiredBanner() {
  const [detail, setDetail] = useState<PlanRequiredDetail | null>(null);
  const location = useLocation();
  useEffect(() => setDetail(null), [location.pathname, location.search]);

  useEffect(() => {
    installPlanRequiredInterceptor();
    const handler = (event: Event) => {
      setDetail((event as CustomEvent<PlanRequiredDetail>).detail);
    };
    window.addEventListener(PLAN_REQUIRED_EVENT, handler);
    return () => window.removeEventListener(PLAN_REQUIRED_EVENT, handler);
  }, []);

  if (!detail) return null;

  return (
    <div
      role="alert"
      data-testid="plan-required-banner"
      className="flex items-center gap-3 border-b border-warn/25 bg-warn-soft px-4 py-2.5 text-sm text-stone-800"
    >
      <Lock className="h-4 w-4 shrink-0 text-warn" aria-hidden="true" />
      <span className="flex-1">
        {detail.message} This action was not completed.{" "}
        <a href={detail.code === "team_plan_required" ? "/#pricing" : "/account"} className="font-semibold text-ink underline underline-offset-2 hover:text-action">
          {detail.code === "team_plan_required" ? "Review Platform & Teams" : detail.code === "plan_activation_required" ? "Activate plan" : "Review workspace plans"}
        </a>
      </span>
      <button
        type="button"
        onClick={() => setDetail(null)}
        className="rounded p-1 text-warn hover:bg-warn/10 focus-visible:outline-2 focus-visible:outline-accent"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}
